import { useEffect, useMemo, useState } from 'react'
import { Shield, Plus, CheckCircle2, Trash2, ExternalLink } from 'lucide-react'
import { api } from '@/lib/api'

type Role =
  | 'LLM'
  | 'EMBEDDINGS'
  | 'EMAIL_PROVIDER'
  | 'SMS_PROVIDER'
  | 'WHATSAPP_PROVIDER'
  | 'VOICE_PROVIDER'
  | 'STORAGE'
  | 'BACKUP'
  | 'REVIEWS'
  | 'HOSTING'
  | 'DNS'
  | 'OTHER'

type Location = 'EU' | 'EU_ADEQUACY' | 'EU_SCC' | 'US' | 'UK' | 'OTHER'

interface SubProcessor {
  id: string
  name: string
  url: string | null
  role: Role
  dataCategories: string[]
  location: Location
  dpaUrl: string | null
  dpaVersion: string | null
  sccVersion: string | null
  tiaCompletedAt: string | null
  tiaDocumentId: string | null
  lastReviewedAt: string | null
  publicListed: boolean
  active: boolean
}

const ROLES: Role[] = [
  'LLM',
  'EMBEDDINGS',
  'EMAIL_PROVIDER',
  'SMS_PROVIDER',
  'WHATSAPP_PROVIDER',
  'VOICE_PROVIDER',
  'STORAGE',
  'BACKUP',
  'REVIEWS',
  'HOSTING',
  'DNS',
  'OTHER',
]

const LOCATIONS: Location[] = ['EU', 'EU_ADEQUACY', 'EU_SCC', 'US', 'UK', 'OTHER']

export function SubProcessorsPage() {
  const [items, setItems] = useState<SubProcessor[]>([])
  const [err, setErr] = useState<string | null>(null)

  // Form draft
  const [name, setName] = useState('')
  const [url, setUrl] = useState('')
  const [role, setRole] = useState<Role>('LLM')
  const [location, setLocation] = useState<Location>('EU')
  const [dataCategories, setDataCategories] = useState('')
  const [dpaUrl, setDpaUrl] = useState('')

  async function load() {
    try {
      const r = await api<{ items: SubProcessor[] }>('/v1/marketing/rgpd/sub-processors')
      setItems(r.items)
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    }
  }

  useEffect(() => {
    void load()
  }, [])

  async function create(e: React.FormEvent) {
    e.preventDefault()
    try {
      await api('/v1/marketing/rgpd/sub-processors', {
        method: 'POST',
        body: JSON.stringify({
          name,
          url: url || undefined,
          role,
          location,
          dataCategories: dataCategories
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean),
          dpaUrl: dpaUrl || undefined,
        }),
      })
      setName('')
      setUrl('')
      setDataCategories('')
      setDpaUrl('')
      void load()
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    }
  }

  async function togglePublicListed(sp: SubProcessor) {
    // Quitar del listado público rompe la transparencia art. 13 sobre ese
    // proveedor — confirmar explícitamente.
    if (sp.publicListed) {
      if (
        !confirm(
          `Retirar "${sp.name}" del listado público elimina la transparencia (RGPD art. 13) sobre este proveedor.\n\n¿Continuar?`,
        )
      ) {
        return
      }
    }
    await api(`/v1/marketing/rgpd/sub-processors/${sp.id}`, {
      method: 'PATCH',
      body: JSON.stringify({ publicListed: !sp.publicListed }),
    })
    void load()
  }

  async function markReviewed(sp: SubProcessor) {
    await api(`/v1/marketing/rgpd/sub-processors/${sp.id}/mark-reviewed`, { method: 'POST' })
    void load()
  }

  async function remove(sp: SubProcessor) {
    if (!confirm(`¿Desactivar "${sp.name}"? Esto lo retira del listado público.`)) return
    await api(`/v1/marketing/rgpd/sub-processors/${sp.id}`, { method: 'DELETE' })
    void load()
  }

  const sorted = useMemo(
    () => [...items].sort((a, b) => Number(b.active) - Number(a.active) || a.name.localeCompare(b.name)),
    [items],
  )

  return (
    <div className="p-6 max-w-6xl">
      <header className="mb-4">
        <h1 className="text-lg font-medium tracking-tight flex items-center gap-2">
          <Shield className="size-5 text-blueprint-700" />
          Sub-procesadores
        </h1>
        <p className="text-xs font-mono text-ink-700/60">
          RGPD art. 28 · matriz de encargados del tratamiento · lista pública en
          /legal/sub-procesadores
        </p>
      </header>

      <form
        onSubmit={(e) => void create(e)}
        className="bp-card p-3 mb-4 grid grid-cols-12 gap-2"
      >
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Nombre del proveedor"
          required
          className="col-span-3 border border-ink-900/15 rounded px-2 py-1.5 text-sm"
        />
        <select
          value={role}
          onChange={(e) => setRole(e.target.value as Role)}
          className="col-span-2 border border-ink-900/15 rounded px-2 py-1.5 text-sm bg-white"
        >
          {ROLES.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
        <select
          value={location}
          onChange={(e) => setLocation(e.target.value as Location)}
          className="col-span-1 border border-ink-900/15 rounded px-2 py-1.5 text-sm bg-white"
        >
          {LOCATIONS.map((l) => (
            <option key={l} value={l}>
              {l}
            </option>
          ))}
        </select>
        <input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://proveedor"
          className="col-span-2 border border-ink-900/15 rounded px-2 py-1.5 text-sm"
        />
        <input
          value={dpaUrl}
          onChange={(e) => setDpaUrl(e.target.value)}
          placeholder="DPA url"
          className="col-span-2 border border-ink-900/15 rounded px-2 py-1.5 text-sm"
        />
        <input
          value={dataCategories}
          onChange={(e) => setDataCategories(e.target.value)}
          placeholder="categorías (coma)"
          className="col-span-2 border border-ink-900/15 rounded px-2 py-1.5 text-sm"
        />
        <button type="submit" className="bp-btn-primary col-span-12 justify-center">
          <Plus className="size-3.5" /> Añadir sub-procesador
        </button>
      </form>

      {err && (
        <div className="text-xs font-mono text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1 mb-3">
          {err}
        </div>
      )}

      <div className="bp-card overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-ink-900/5 text-ink-700/70">
            <tr>
              <th className="text-left px-3 py-2">Proveedor</th>
              <th className="text-left px-3 py-2">Rol</th>
              <th className="text-left px-3 py-2">Ubicación</th>
              <th className="text-left px-3 py-2">DPA</th>
              <th className="text-left px-3 py-2">Última revisión</th>
              <th className="text-left px-3 py-2">Público</th>
              <th className="text-right px-3 py-2">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((sp) => (
              <tr
                key={sp.id}
                className={[
                  'border-t border-ink-900/5',
                  sp.active ? '' : 'opacity-50',
                ].join(' ')}
              >
                <td className="px-3 py-2">
                  <div className="font-medium">{sp.name}</div>
                  {sp.url && (
                    <a
                      href={sp.url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[11px] text-blueprint-700 inline-flex items-center gap-0.5"
                    >
                      {sp.url} <ExternalLink className="size-3" />
                    </a>
                  )}
                  {sp.dataCategories.length > 0 && (
                    <div className="text-[10px] font-mono text-ink-700/60 mt-0.5">
                      {sp.dataCategories.join(' · ')}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2 font-mono text-[11px]">{sp.role}</td>
                <td className="px-3 py-2 font-mono text-[11px]">{sp.location}</td>
                <td className="px-3 py-2">
                  {sp.dpaUrl ? (
                    <a
                      href={sp.dpaUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-blueprint-700 inline-flex items-center gap-0.5"
                    >
                      DPA <ExternalLink className="size-3" />
                    </a>
                  ) : (
                    <span className="text-ink-700/40">—</span>
                  )}
                </td>
                <td className="px-3 py-2 font-mono text-[11px]">
                  {sp.lastReviewedAt ? new Date(sp.lastReviewedAt).toLocaleDateString('es-ES') : '—'}
                </td>
                <td className="px-3 py-2">
                  <label className="inline-flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={sp.publicListed}
                      onChange={() => void togglePublicListed(sp)}
                    />
                    <span className="text-[11px] text-ink-700/60">listado</span>
                  </label>
                </td>
                <td className="px-3 py-2 text-right">
                  <button
                    onClick={() => void markReviewed(sp)}
                    className="bp-btn inline-flex items-center gap-1 text-[11px]"
                    title="Marcar como revisado"
                  >
                    <CheckCircle2 className="size-3" /> Revisado
                  </button>
                  <button
                    onClick={() => void remove(sp)}
                    className="bp-btn inline-flex items-center gap-1 text-[11px] ml-1 text-red-700"
                    title="Desactivar"
                  >
                    <Trash2 className="size-3" />
                  </button>
                </td>
              </tr>
            ))}
            {sorted.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-ink-700/50">
                  Sin sub-procesadores registrados.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
